import type { SessionInfo } from '@ana/contracts';

/**
 * Identity kept on the device for offline drafting (D-13). It holds no token and, per unit, only the permissions that
 * matter offline: units where the user could not edit a movement are not kept at all, so offline drafting is offered
 * exactly where the server had granted `movement.edit`. Every server action stays disabled while offline.
 */
export interface CachedIdentity {
  userId: string;
  displayName: string;
  units: SessionInfo['units'];
  /** Last time the server confirmed this identity (ISO). */
  at: string;
}

/** D-13: 72 h after the last confirmation by the server the cached identity is no longer accepted. */
export const OFFLINE_IDENTITY_TTL_MS = 72 * 3_600_000;
const OFFLINE_PERMISSIONS = ['movement.read', 'movement.edit'];

export function offlineUnits(units: SessionInfo['units']): SessionInfo['units'] {
  return units
    .filter((u) => u.permissions.includes('movement.edit'))
    .map((u) => ({ ...u, permissions: u.permissions.filter((p) => OFFLINE_PERMISSIONS.includes(p)) }));
}

/** What to store after a live session; null when no unit allows drafting (nothing is kept). */
export function toCachedIdentity(s: SessionInfo, now: Date): CachedIdentity | null {
  const units = offlineUnits(s.units);
  if (units.length === 0) return null;
  return { userId: s.user.id, displayName: s.user.displayName, units, at: now.toISOString() };
}

/**
 * Reads a stored identity. Expired, malformed or future-dated entries are refused, and the permissions are filtered
 * again, so an entry written by an older version (with every permission of the unit) cannot widen what is offered.
 */
export function parseCachedIdentity(raw: string | null, now: Date): CachedIdentity | null {
  if (!raw) return null;
  let v: Partial<CachedIdentity>;
  try {
    v = JSON.parse(raw) as Partial<CachedIdentity>;
  } catch {
    return null;
  }
  if (typeof v.userId !== 'string' || typeof v.at !== 'string' || !Array.isArray(v.units)) return null;
  const at = Date.parse(v.at);
  if (Number.isNaN(at) || at > now.getTime() + 5 * 60_000) return null;
  if (now.getTime() - at >= OFFLINE_IDENTITY_TTL_MS) return null;
  const units = offlineUnits(
    v.units.filter((u) => u && typeof u.id === 'string' && Array.isArray(u.permissions)),
  );
  if (units.length === 0) return null;
  return { userId: v.userId, displayName: String(v.displayName ?? ''), units, at: v.at };
}

/** Offline, only drafting is allowed, and only where the stored permissions had it. */
export function offlineCan(unit: SessionInfo['units'][number] | undefined, permission: string): boolean {
  return Boolean(unit) && OFFLINE_PERMISSIONS.includes(permission) && unit!.permissions.includes(permission);
}
